import type { AIGenerationConversation } from "@/integrations/backend/program-generation";
import { ApiRequestError } from "@/lib/api-request-error";

const ASSIGNED_WORKOUT_EDIT_SEED_PREFIX = "Current workout snapshot (reference only):";

export type FormatErrorForCopyOptions = {
  /** Latest user chat input relevant to the failed AI step. */
  aiInputText?: string | null;
  /** AI output for the failed step: assistant text, API error body, or other resulting model output. */
  aiOutputText?: string | null;
};

/** Short message for UI (toast line, etc.). */
export function getPrimaryErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string" && error.trim()) return error;
  return fallback;
}

function primaryMessage(error: unknown, fallback: string): string {
  return getPrimaryErrorMessage(error, fallback);
}

function normalizeText(text: string | null | undefined): string | null {
  const trimmed = text?.trim();
  return trimmed ? trimmed : null;
}

export function getApiErrorResponseBody(error: unknown): string | null {
  if (!(error instanceof ApiRequestError)) return null;
  return normalizeText(error.responseBody);
}

export function lastUserInputForCopy(
  conversation: Pick<AIGenerationConversation, "messages"> | null | undefined
): string | null {
  const messages = conversation?.messages;
  if (!messages?.length) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "user") return normalizeText(messages[i].content);
  }
  return null;
}

/**
 * Plain-text bundle for clipboard: user-facing message, JS stack if any, HTTP body if any,
 * plus AI input/output context when available.
 */
export function formatErrorForClipboard(
  error: unknown,
  fallback: string,
  options?: FormatErrorForCopyOptions
): string {
  const sections: string[] = [];
  const msg = primaryMessage(error, fallback);
  sections.push(`Error: ${msg}`);

  if (error instanceof Error && error.stack) {
    sections.push("");
    sections.push("Stack trace:");
    sections.push(error.stack);
  }

  const aiInputText = normalizeText(options?.aiInputText);
  if (aiInputText) {
    sections.push("");
    sections.push("AI input:");
    sections.push(aiInputText);
  }

  const aiOutputText = normalizeText(options?.aiOutputText);
  if (aiOutputText) {
    sections.push("");
    sections.push("AI output:");
    sections.push(aiOutputText);
  }

  const body = getApiErrorResponseBody(error);
  if (body && body !== msg && body !== aiOutputText) {
      sections.push("");
      sections.push("HTTP response body:");
      sections.push(body);
  }

  return sections.join("\n");
}

/** Latest substantive assistant text; skips the assigned-workout seed system message. */
export function lastAssistantRawOutputForCopy(
  conversation: Pick<AIGenerationConversation, "chat_type" | "messages"> | null | undefined
): string | null {
  const messages = conversation?.messages;
  if (!messages?.length) return null;
  const chatType = conversation.chat_type;
  if (chatType === "assigned_workout_edit") {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== "assistant") continue;
      if (m.content.startsWith(ASSIGNED_WORKOUT_EDIT_SEED_PREFIX)) continue;
      return normalizeText(m.content);
    }
    return null;
  }
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant") return normalizeText(messages[i].content);
  }
  return null;
}

export function getAiOutputTextForError(
  error: unknown,
  assistantOutput?: string | null
): string | null {
  return (
    normalizeText(assistantOutput) ??
    getApiErrorResponseBody(error) ??
    normalizeText(getPrimaryErrorMessage(error, ""))
  );
}

export function buildChatErrorCopyOptions(
  error: unknown,
  conversation: Pick<AIGenerationConversation, "chat_type" | "messages"> | null | undefined,
  aiInputText?: string | null
): FormatErrorForCopyOptions {
  const assistantOutput = lastAssistantRawOutputForCopy(conversation);
  return {
    aiInputText: normalizeText(aiInputText) ?? lastUserInputForCopy(conversation),
    aiOutputText: getAiOutputTextForError(error, assistantOutput),
  };
}

/** Clipboard text for client-side parse failures (modal banner). */
export function formatParseErrorForClipboard(
  parseError: string,
  options?: FormatErrorForCopyOptions
): string {
  const parts = [`Parse error: ${parseError}`];
  const aiInputText = normalizeText(options?.aiInputText);
  if (aiInputText) {
    parts.push("");
    parts.push("AI input:");
    parts.push(aiInputText);
  }
  const aiOutputText = normalizeText(options?.aiOutputText);
  if (aiOutputText) {
    parts.push("");
    parts.push("AI output:");
    parts.push(aiOutputText);
  }
  return parts.join("\n");
}
